/**
 * Shared persona theme utilities.
 * Used by AIChatbot and ArticlePage (and any future component).
 * The LLM returns a theme object; this module applies it to the page.
 */

export interface PersonaTheme {
    name?: string;
    primary?: string;
    secondary?: string;
    bg?: string;
    surface?: string;
    text?: string;
    muted?: string;
    border?: string;
    font?: string;
    borderRadius?: string;
    bgPattern?: string;
    chatIcon?: string;
    heroText?: string;
    // LLM-generated raw CSS injected directly — no hard-coded maps
    customCSS?: string;
}

function loadFont(fontName: string): void {
    const id = `persona-font-link-${fontName.replace(/\s+/g, '-')}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, '+')}:wght@400;600;700&display=swap`;
    document.head.appendChild(link);
}

function injectStyle(id: string, css: string): void {
    let el = document.getElementById(id) as HTMLStyleElement | null;
    if (!el) { el = document.createElement('style'); el.id = id; document.head.appendChild(el); }
    el.textContent = css;
}

export function applyPersonaTheme(theme: PersonaTheme): void {
    const root = document.documentElement;
    const primary = theme.primary || '#bd93f9';
    const bg = theme.bg || '#0d0f18';
    const radius = theme.borderRadius || '12px';
    const surface = theme.surface || '#1a1b2e';
    const text = theme.text || '#f1f0ff';
    const muted = theme.muted || '#8892b0';

    // ── CSS custom properties ────────────────────────────────────────────
    root.style.setProperty('--bg', bg);
    root.style.setProperty('--bg-card', surface);
    root.style.setProperty('--text-primary', text);
    root.style.setProperty('--text-muted', muted);
    root.style.setProperty('--persona-primary', primary);
    root.style.setProperty('--persona-radius', radius);
    if (theme.secondary) root.style.setProperty('--persona-secondary', theme.secondary);

    // Wire border so Home's card border (reads --border) + filter pills (read --filter-border/bg/text)
    // all respond to the persona — not just the prefixed --persona-border.
    const border = theme.border || `${primary}33`;
    root.style.setProperty('--border', border);
    root.style.setProperty('--persona-border', border);
    // Filter pills: use primary-tinted variants so they feel part of the theme
    root.style.setProperty('--filter-border', `${primary}66`);
    root.style.setProperty('--filter-bg', `${primary}11`);
    root.style.setProperty('--filter-text', theme.muted || muted);

    // ── Font ────────────────────────────────────────────────────────────
    if (theme.font) {
        loadFont(theme.font);
        injectStyle('persona-font-style', `
            body, h1, h2, h3, button, input, select, textarea {
                font-family: '${theme.font}', sans-serif !important;
            }
        `);
    }

    // ── Background pattern ───────────────────────────────────────────────
    if (theme.bgPattern && theme.bgPattern !== 'solid') {
        const patterns: Record<string, [string, string]> = {
            gradient: [`linear-gradient(135deg, ${bg} 0%, ${primary}33 100%)`, ''],
            dots: [`radial-gradient(${primary}55 1.5px, ${bg} 1.5px)`, '20px 20px'],
            grid: [`linear-gradient(${primary}22 1px, transparent 1px), linear-gradient(90deg, ${primary}22 1px, transparent 1px)`, '28px 28px'],
            scanlines: [`repeating-linear-gradient(0deg, transparent, transparent 2px, ${primary}08 2px, ${primary}08 4px)`, ''],
        };
        const [img, size] = patterns[theme.bgPattern] || ['', ''];
        document.body.style.backgroundImage = img;
        document.body.style.backgroundSize = size;
    } else {
        document.body.style.backgroundImage = '';
        document.body.style.backgroundSize = '';
    }

    // ── LLM-generated custom CSS (fully dynamic, no hard-coded maps) ─────
    // The API returns raw CSS the LLM thinks fits the persona.
    // This can override cards, buttons, links — anything.
    if (theme.customCSS) {
        injectStyle('persona-custom-css', theme.customCSS);
    }

    // ── Broadcast to React components via CustomEvent ────────────────────
    // Components (ArticlePage, AIChatbot) listen for this to update state
    // without needing a DOM mutation that React will overwrite.
    window.dispatchEvent(new CustomEvent('persona-applied', {
        detail: { heroText: theme.heroText || '', chatIcon: theme.chatIcon || '', name: theme.name || '' },
    }));
}

export function resetPersonaTheme(): void {
    const root = document.documentElement;
    [
        '--bg', '--bg-card', '--text-primary', '--text-muted',
        '--persona-primary', '--persona-secondary', '--persona-radius',
        '--persona-border', '--border',
        '--filter-border', '--filter-bg', '--filter-text',
    ].forEach(v => root.style.removeProperty(v));

    document.body.style.backgroundImage = '';
    document.body.style.backgroundSize = '';

    [
        'persona-font-style', 'persona-custom-css',
    ].forEach(id => document.getElementById(id)?.remove());

    document.querySelectorAll('[id^="persona-font-link-"]').forEach(el => el.remove());

    const btn = document.querySelector('button[aria-label="Open AbhibotAI"]') as HTMLElement | null;
    if (btn) { btn.textContent = '>_'; btn.style.fontSize = '13px'; }

    // Re-trigger ThemeContext + notify React components
    window.dispatchEvent(new StorageEvent('storage', { key: 'theme' }));
    window.dispatchEvent(new CustomEvent('persona-applied', {
        detail: { heroText: '', chatIcon: '', name: '' },
    }));
}
